/** @file clipEntries: the rows of the clip list's menu (new, rename, duplicate, set meta, delete). */
import { uniqueNodeName } from '../../panels/tree/uniqueNodeName';
import { duplicateClipCommands } from '../../panels/timeline/duplicateClipCommands';
import type { EditorStore } from '../../state/editorTypes';
import type { ClipMenuCtx, MenuEntry } from './registryTypes';

type Entry = MenuEntry<ClipMenuCtx>;
const onClip = (c: ClipMenuCtx): boolean => c.clip !== null;

/** Creates `clip`, `clip2`, ... (24 frames at 24 fps) in `category` and puts it on the playhead. */
function newClip(store: EditorStore, category: string): void {
  const s = store.getState();
  const name = uniqueNodeName(s.puppet.doc?.clips ?? {}, 'clip');
  void s.dispatch([{ name: 'clip.create', input: { name, frames: 24, fps: 24, loop: true, category } }], `new clip ${name}`);
  s.setPlayhead({ clip: name, frame: 0, playing: false });
}

/** Deletes `name` after `confirm`; clears the playhead clip when it was this one. */
function deleteClip(store: EditorStore, name: string, confirm: (text: string) => boolean): void {
  if (!confirm(`Delete clip "${name}"?`)) return;
  const s = store.getState();
  void s.dispatch([{ name: 'clip.delete', input: { clip: name } }], `delete clip ${name}`);
  if (s.playhead.clip === name) s.setPlayhead({ clip: null, frame: 0, playing: false });
}

/** New clip always; the rest only on a clip row. */
export const CLIP_ENTRIES: Entry[] = [
  { kind: 'item', id: 'clip.new', label: () => 'New clip', run: (c) => { newClip(c.store, c.category); } },
  { kind: 'separator' },
  { kind: 'item', id: 'clip.rename', label: () => 'Rename...', when: onClip, run: (c) => { if (c.clip !== null) c.ui.rename(c.clip); } },
  {
    kind: 'item', id: 'clip.duplicate', label: () => 'Duplicate', when: onClip,
    run: (c) => {
      const s = c.store.getState();
      if (c.clip !== null) void s.dispatch(duplicateClipCommands(s.puppet.doc?.clips ?? {}, c.clip), `duplicate ${c.clip}`);
    },
  },
  { kind: 'item', id: 'clip.meta', label: () => 'Set meta...', when: onClip, run: (c) => { if (c.clip !== null) c.ui.meta(c.clip); } },
  { kind: 'separator' },
  { kind: 'item', id: 'clip.delete', label: () => 'Delete', when: onClip, run: (c) => { if (c.clip !== null) deleteClip(c.store, c.clip, c.ui.confirm); } },
];
